import {
  SearchInput,
  TableCellContentRenderer,
} from '@/components/basic';
import {
  ConfirmOperationModal,
  groupSharedKey
} from '@/components/hours/rule/ListHoursRules';
import { useI18n } from '@/hooks/useI18n';
import { classNames } from '@/lib/classNames';
import {
  IHour,
  cover24Hours,
  parseDateOnlyString,
  parseTimeOnly,
  prettyTimeOnly
} from '@gettactic/api';
import { useAuthenticated } from '@gettactic/hooks';
import { format } from 'date-fns';
import React, { useMemo, useState } from 'react';
import { MdDelete } from 'react-icons/md';
import { CellProps, Column, Row } from 'react-table';
import { Checkbox } from 'reakit';
import { HourRulesTableView } from './HourRulesTableView';

export interface SharedHourRulesTableProps {
  loading: boolean;
  data: IHour[];
  inGroups: { [groupKey: string]: IHour[] };
  setOperationalModal: React.Dispatch<ConfirmOperationModal | null>;
  updateFunction: ((hour: IHour, newWeight: number) => Promise<void>) | null;
  invalidateQuery: () => Promise<void>;
}

export function SharedHourRulesTable({
  data: rawData,
  inGroups,
  setOperationalModal,
  updateFunction,
  invalidateQuery
}: SharedHourRulesTableProps): JSX.Element {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const { templates } = useI18n(authenticatedUser);
  const [query, setQuery] = useState('');
  const [bulk, setBulk] = useState<string[]>([]);
  const data = useMemo(() => {
    const q = query.toLowerCase().trim();
    return q.length
      ? rawData.filter((x) => x.name?.toLowerCase().includes(q))
      : rawData;
  }, [query, rawData, inGroups]);
  const selectedAll = data.length ? data.length === bulk.length : false;
  const visibleSelected = data.filter(
    (x) => bulk.indexOf(groupSharedKey(x)) > -1
  );
  const setDeleteModal = (h: IHour | null) => {
    if (h) {
      setOperationalModal({
        operation: 'delete',
        applyTo: inGroups[groupSharedKey(h)]
      });
    }
  };
  const columns: Column<IHour>[] = [
    {
      Header() {
        return (
          <div className="px-2">
            <Checkbox
              className="rounded text-primary focus:ring-0"
              checked={selectedAll}
              onClick={() => {
                if (!selectedAll) {
                  setBulk((x) => data.map((x) => groupSharedKey(x)));
                } else {
                  setBulk([]);
                }
              }}
            />
          </div>
        );
      },
      accessor: undefined,
      id: 'selected',
      Cell({ row: { original: hour } }: { row: Row<IHour> }) {
        const selected = bulk.indexOf(groupSharedKey(hour)) > -1;
        return (
          <div className="flex items-center justify-start space-x-2 px-2">
            <Checkbox
              className="rounded text-primary focus:ring-0"
              checked={selected}
              onClick={() => {
                if (selected) {
                  setBulk((x) => x.filter((s) => s !== groupSharedKey(hour)));
                } else {
                  setBulk((x) => [...x, groupSharedKey(hour)]);
                }
              }}
            />
          </div>
        );
      }
    },
    {
      Header() {
        return <div className="px-2">Priority</div>;
      },
      id: 'handle-placeholder',
      accessor: undefined,
      Cell: TableCellContentRenderer.DndHandle
    },
    {
      Header() {
        return <div className="">Name</div>;
      },
      accessor: 'name',
      Cell: ({ value }: CellProps<IHour, "name">) => (
        <div
          title={value ?? undefined}
          className="w-14 overflow-hidden overflow-ellipsis text-sm sm:w-14 md:w-40"
        >
          {value}
        </div>
      )
    },
    {
      Header() {
        return <div>Date</div>;
      },
      accessor: undefined,
      id: 'dates',
      Cell({ row: { original: hour } }: { row: Row<IHour> }) {
        const since = hour.since ? parseDateOnlyString(hour.since) : null;
        const until = hour.until ? parseDateOnlyString(hour.until) : null;
        const sinceShort = since
          ? format(since, templates.displayDateShort)
          : null;
        const untilShort = until
          ? format(until, templates.displayDateShort)
          : null;
        return (
          <div className="text-sm">
            {!since && !until ? (
              <span className="font-bold">Always</span>
            ) : since && sinceShort === untilShort ? (
              <span>{format(since, templates.displayDateShort)}</span>
            ) : (
              <>
                <span>
                  {since ? format(since, templates.displayDateShort) : null}
                </span>
                <span>-</span>
                {until ? (
                  <div>{format(until, templates.displayDateShort)}</div>
                ) : null}
              </>
            )}
          </div>
        );
      }
    },
    {
      Header() {
        return (
          <div>
            <div>Hours</div>
          </div>
        );
      },
      accessor: undefined,
      id: 'hours',
      Cell({ row: { original: hour } }: { row: Row<IHour> }) {
        return (
          <div className="px-2 text-sm">
            {cover24Hours(
              parseTimeOnly(hour.start_time),
              parseTimeOnly(hour.end_time)
            ) ? (
              <div className="">All day</div>
            ) : (
              <div className="w-16">
                <span>{prettyTimeOnly(hour.start_time)}</span>
                <span>{prettyTimeOnly(hour.end_time)}</span>
              </div>
            )}
          </div>
        );
      }
    },
    {
      Header() {
        return (
          <div>
            <div title={'Availability'} className="px-2">
              Availability
            </div>
          </div>
        );
      },
      accessor: undefined,
      id: 'visibility',
      Cell({ row: { original: hour } }: { row: Row<IHour> }) {
        return (
          <div className="px-2 text-center text-sm">
            {hour.is_available ? (
              <div className="w-16 rounded-full bg-green-600 px-1 py-1 text-white">
                Open
              </div>
            ) : (
              <div className="w-16 rounded-full bg-red-200 px-1 py-1 text-red-900">
                Closed
              </div>
            )}
          </div>
        );
      }
    },
    {
      Header() {
        return <span className="" />;
      },
      id: 'edit-menu-placeholder',
      accessor: undefined,
      Cell: ({ row }: { row: Row<IHour> }) =>
        TableCellContentRenderer.EditDeleteControls({
          row,
          setDeleteModal,
          setEditModal: (h) => {
            if (h) {
              setOperationalModal({
                operation: 'edit',
                applyTo: inGroups[groupSharedKey(h)]
              });
            }
          }
        })
    }
  ];

  return (
    <div>
      <div className="mt-4 mr-0 flex items-center justify-between gap-16">
        <SearchInput query={query} setQuery={setQuery} />
        <div className="flex flex-nowrap">
          {visibleSelected.length ? (
            <p>Selected {visibleSelected.length}</p>
          ) : null}
          <button
            disabled={bulk.length === 0}
            onClick={(ev) => {
              ev.preventDefault();
              const deleteAll: IHour[] = [];
              visibleSelected.forEach((x) =>
                deleteAll.push(...inGroups[groupSharedKey(x)])
              );
              setOperationalModal({ operation: 'delete', applyTo: deleteAll });
            }}
            className={classNames(
              visibleSelected.length
                ? 'text-gray-600 hover:text-gray-800'
                : 'text-gray-100',
              'px-2'
            )}
          >
            <MdDelete className="h-5 w-5" />
          </button>
        </div>
      </div>

      <HourRulesTableView
        reorderable
        columns={columns}
        list={data}
        invalidateQuery={invalidateQuery}
        updateFunction={updateFunction}
      />
    </div>
  );
}
